import {
  BizNestClientError, EhttpController, FrontendLogger, IehttpControllerConfig
} from '@biz-nest/core-client';

/**
 * Social login endpoints exposed by the backend for client-initiated OAuth flows.
 *
 * @public
 */
export interface IsocialCallUrls {

  /** OAuth entrypoint for Google login. */
  google: string;

  /** OAuth entrypoint for Facebook login. */
  facebook: string;
}

/**
 * Thin wrapper around {@link EhttpController} to manage Auth API calls and
 * optional social login URLs. Must be initialized once per app session.
 *
 * @public
 */
export class AuthClient {
  private static http: EhttpController;
  private static localSocialCallUrls?: IsocialCallUrls;

  /**
   * Creates an instance of {@link AuthClient} with environment validation.
   *
   * @param config - Configuration passed to the underlying {@link EhttpController}.
   * @param socialCallUrls - Optional social login URLs (Google/Facebook).
   * @throws {@link BizNestClientError} If configuration is invalid.
   */
  constructor(config: IehttpControllerConfig, socialCallUrls?: IsocialCallUrls) {
    if (!config || typeof config !== 'object') {
      throw new BizNestClientError('config must be a valid object');
    }


    FrontendLogger.debug('AuthClient initialized');
    AuthClient.http = new EhttpController(config);
    AuthClient.localSocialCallUrls = socialCallUrls;
  }

  /**
   * Provides the initialized HTTP controller.
   *
   * @returns The singleton {@link EhttpController} instance.
   * @throws {@link BizNestClientError} If the client has not been initialized via constructor.
   */
  static get ehttp() {
    if (!AuthClient.http) {
      throw new BizNestClientError('HTTP client not initialized. Call new AuthClient.');
    }

    return AuthClient.http;
  }

  /**
 * Provides configured social login endpoints.
 *
 * @returns Social OAuth entrypoints for Google and Facebook.
 * @throws {@link BizNestClientError} If social URLs were not provided at construction.
 */
  static get socialCallUrls() {
    if (!AuthClient.localSocialCallUrls) {
      throw new BizNestClientError('Social call URLs not provided at construction..');
    }

    return AuthClient.localSocialCallUrls;
  }
}
